import { ActionSheetController, AlertController, ModalController, NavParams, ViewController } from 'ionic-angular';
import { Component } from '@angular/core';
import { Subject } from 'rxjs/Rx';
import { forkJoin } from 'rxjs/observable/forkJoin';

import { EtaWarningModalComponent } from '../../components/eta-warning-modal/eta-warning-modal';
import { FillerMessageTypes } from '../../providers/message/message';
import { LoadingAlertProvider } from '../../providers/loading-alert/loading-alert';
import { OpsOrderModel } from '../../models/order.ops';
import { OpsProvider } from '../../providers/ops/ops';
import { OrderCancelReason, OrderStatus } from './../../models/order.ops';
import { TruckModel } from './../../models/truck';

declare const document: any;


@Component({
  selector: 'page-order-detail',
  templateUrl: 'order-detail.html',
})
export class OrderDetailPage {
  order: OpsOrderModel;
  vehicleHeaderName: string = '';
  truck: TruckModel;

  private unsubscribe: Subject<void> = new Subject();

  constructor(
    navParams: NavParams,
    private modalCtlr: ModalController,
    private opsService: OpsProvider,
    private loadingAlert: LoadingAlertProvider,
    private actionSheetCtlr: ActionSheetController,
    private viewCtlr: ViewController,
    private alertCtlr: AlertController
  ) {
    this.order = navParams.get('order');
    this.truck = navParams.get('truck');
    this.opsService
      .subscribeToOrder(this.order.uid)
      .takeUntil(this.unsubscribe)
      .subscribe(order => {
        if (order) {
          this.order = order;
        }
        if (
          this.order &&
          this.order.vehicle &&
          this.order.vehicle.vehicleModel &&
          this.order.vehicle.color
        ) {
          this.vehicleHeaderName = `${this.order.vehicle.vehicleModel.model} (${this.order.vehicle.color})`;
        }
      });
    this.refreshOrder();
    this.truck = navParams.get('truck');
  }

  ionViewDidLoad() {
    console.log('ionViewDidLoad OrderDetailPage');
    this.refreshOrderOnVisibilityChange();
  }

  ionViewWillEnter() {
    this.refreshOrder();
  }

  private listener: EventListener;

  ngOnDestroy() {
    if (this.listener) {
      removeEventListener('visibilitychange', this.listener);
    }
  }

  refreshOrderOnVisibilityChange() {
    this.listener = () => {
      if (document.visibilityState === 'visible') {
        this.refreshOrder();
      }
    };
    addEventListener('visibilitychange', this.listener);
  }

  // Actions

  didTapSendETA() {
    this.loadingAlert.showLoader();
    this.opsService
      .sendETAForOrder(this.order)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        order => (this.order = order),
        error =>
          this.loadingAlert.showToastAlert('Error while trying to send eta')
      );
  }

  didTapStartDriving() {
    this.loadingAlert.showLoader();
    this.opsService
      .startDrivingToOrder(this.order)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        order => (this.order = order),
        error =>
          this.loadingAlert.showToastAlert(
            'Error while trying to start driving'
          )
      );
  }

  didTapStartDrivingAndSendEta() {
    this.order.outsideTargetTimeWindow(new Date())
      ? this.showETAWarningModal()
      : this.startDrivingAndSendETA();
  }

  showETAWarningModal() {
    const warningModal = this.modalCtlr.create(EtaWarningModalComponent, null, {
      enableBackdropDismiss: false,
    });
    warningModal.onDidDismiss(proceed => {
      if (!proceed) {
        return;
      }
      this.startDrivingAndSendETA();
    });
    warningModal.present();
  }

  startDrivingAndSendETA() {
    this.loadingAlert.showLoader();
    const reqSendETA = this.opsService.sendETAForOrder(this.order);
    const reqStartDriving = this.opsService.startDrivingToOrder(this.order);
    const requests = [reqSendETA, reqStartDriving];
    forkJoin(requests)
      .takeUntil(this.unsubscribe)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(orders => (this.order = orders[1]));
  }

  didTapMore() {
    let buttons = [
      {
        text: 'Cancel Order',
        handler: () => this.didTapCancel(),
      },
      {
        text: 'Cancel',
        role: 'cancel',
      },
    ];
    if (this.order.pumpingEta) {
      buttons.push({
        text: 'Clear ETA',
        handler: () => this.didTapClearETA(),
      });
    } else if (this.order.status === OrderStatus.Claimed) {
      buttons.push({
        text: 'Send ETA',
        handler: () => this.didTapSendETA(),
      });
    }
    let actionSheet = this.actionSheetCtlr.create({ buttons: buttons });
    actionSheet.present();
  }

  didTapClearETA() {
    this.loadingAlert.showLoader();
    this.opsService
      .clearETAForOrder(this.order)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        () => {
          this.loadingAlert.showToastConfirmation('Cleared ETA!');
        },
        error => {
          this.loadingAlert.showToastAlert('Error clearing eta');
        }
      );
  }

  didTapCancel() {
    let alert = this.alertCtlr.create({
      title: 'Why are you cancelling this order?',
      inputs: [
        {
          type: 'radio',
          label: 'Order Placeholder',
          value: OrderCancelReason.Placeholder,
        },
        {
          type: 'radio',
          label: 'Owner Missing',
          value: OrderCancelReason.OwnerMissing,
        },
      ],
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Save',
          handler: data => {
            this.cancelOrder(data);
          },
        },
      ],
    });
    alert.present();
  }

  // Data

  refreshOrder() {
    this.opsService.getOrder(this.order.uid);
  }

  cancelOrder(reason: OrderCancelReason) {
    if (reason === OrderCancelReason.OwnerMissing) {
      const didSendMessage = this.opsService.didSendMessageForOrder(
        this.order,
        FillerMessageTypes.OwnerRequired
      );
      if (!didSendMessage) {
        this.showSendMessageAndWaitAlert(reason);
        return;
      }
    }

    this.loadingAlert.showLoader();
    this.opsService
      .cancelOrder(this.order, reason)
      .finally(() => this.loadingAlert.dismissLoader())
      .subscribe(
        () => {
          this.viewCtlr.dismiss();
          this.loadingAlert.showToastConfirmation('Order cancelled!');
        },
        error => {
          this.loadingAlert.showToastAlert('Error cancelling order');
        }
      );
  }

  showSendMessageAndWaitAlert(reason: OrderCancelReason) {
    this.loadingAlert.presentAlert(
      `Before cancelling this order, please send a ${reason.replace(
        /\_/g,
        ' '
      )} message to the customer and wait 10 minutes.`,
      null
    );
  }

  // Helpers

  showEtaButton() {
    return (
      this.order.status === 'pending' &&
      !this.order.virtual
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/order-detail/order-detail.ts